/**
 * Prosumer Calculator - CSV Import Hook
 *
 * Manages CSV file upload, parsing, and extraction of hourly/monthly data.
 * Supports Tauron eLicznik CSV format (semicolon-delimited).
 */

import { useState, useCallback } from 'react';
import type { CSVImportData } from '../types';

// =============================================================================
// CSV PARSER — Tauron eLicznik format (7 columns, semicolon-delimited)
//
// Columns: Nr platnika; Nr punktu poboru energii; Rodzaj energii;
//          Data i godzina; Strefa; Wartosc[kWh/kvar]; Kompletnosc[%]
//
// Energy types:
//   "Energia czynna pobrana" / "Energia czynna" → consumption
//   "Energia czynna oddana" → export (prosumer)
// =============================================================================

// Prefix-based matching to handle variants like "netto", "brutto"
const EXPORT_PREFIX = 'energia czynna oddana';
const CONSUMPTION_PREFIXES = ['energia czynna pobrana', 'energia czynna'];

interface ParsedRow {
  month: number; // 1-based
  dayOfMonth: number;
  hour: number;
  kwh: number;
}

function parseCSVText(text: string): CSVImportData {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);

  if (lines.length < 2) {
    throw new Error('Plik CSV jest pusty lub zawiera zbyt mało danych');
  }

  // Validate header contains semicolons (Tauron format)
  const header = lines[0];
  if (!header.includes(';')) {
    throw new Error('Nieprawidłowy format CSV. Oczekiwany format Tauron eLicznik (separator: średnik)');
  }

  const consumptionRows: ParsedRow[] = [];
  const exportRows: ParsedRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(';').map((c) => c.trim());

    // Tauron eLicznik has 7 columns; need at least 6 for value
    if (cols.length < 6) continue;

    // Column 2: Rodzaj energii (energy type) — prefix match for "netto"/"brutto" variants
    const energyType = cols[2].toLowerCase();
    // Check export FIRST (more specific: "oddana" before generic "czynna")
    const isExport = energyType.startsWith(EXPORT_PREFIX);
    const isConsumption = !isExport && CONSUMPTION_PREFIXES.some((p) => energyType.startsWith(p));
    if (!isExport && !isConsumption) continue;

    // Column 3: Data i godzina — format "YYYY-MM-DD HH:MM"
    const dateTimeStr = cols[3];
    const dtMatch = dateTimeStr.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):\d{2}$/);
    if (!dtMatch) continue;

    const month = parseInt(dtMatch[2], 10);
    const dayOfMonth = parseInt(dtMatch[3], 10);
    const hour = parseInt(dtMatch[4], 10);

    if (isNaN(month) || isNaN(dayOfMonth) || isNaN(hour)) continue;

    // Column 5: Wartosc[kWh/kvar] — Polish decimal (comma → dot)
    const rawKwh = parseFloat(cols[5].replace(',', '.'));
    if (isNaN(rawKwh)) continue;

    // Column 6: Kompletnosc[%] — rescale if < 100%
    let kwh = rawKwh;
    if (cols.length >= 7) {
      const completeness = parseFloat(cols[6].replace(',', '.'));
      if (!isNaN(completeness) && completeness > 0 && completeness < 100) {
        kwh = (rawKwh * 100) / completeness;
      }
    }

    const row: ParsedRow = { month, dayOfMonth, hour, kwh };
    if (isExport) {
      exportRows.push(row);
    } else {
      consumptionRows.push(row);
    }
  }

  if (consumptionRows.length === 0 && exportRows.length === 0) {
    throw new Error('Nie znaleziono prawidłowych danych w pliku CSV');
  }

  // Aggregate monthly totals
  const monthlyConsumptionKwh = new Array(12).fill(0);
  const monthlyExportKwh = new Array(12).fill(0);

  for (const row of consumptionRows) {
    const idx = row.month - 1;
    if (idx >= 0 && idx < 12) monthlyConsumptionKwh[idx] += row.kwh;
  }
  for (const row of exportRows) {
    const idx = row.month - 1;
    if (idx >= 0 && idx < 12) monthlyExportKwh[idx] += row.kwh;
  }

  const totalExport = monthlyExportKwh.reduce((a: number, b: number) => a + b, 0);

  return {
    monthlyConsumptionKwh,
    monthlyExportKwh,
    hasProsumerData: totalExport > 0,
    hourlyConsumptionData: consumptionRows.map((r) => ({
      hour: r.hour,
      dayOfMonth: r.dayOfMonth,
      month: r.month - 1, // 0-based for backend
      consumptionKwh: r.kwh,
    })),
    hourlyExportData: exportRows.map((r) => ({
      hour: r.hour,
      dayOfMonth: r.dayOfMonth,
      month: r.month - 1, // 0-based for backend
      exportKwh: r.kwh,
    })),
  };
}

// =============================================================================
// RETURN TYPE
// =============================================================================

export interface UseCSVImportReturn {
  file: File | null;
  data: CSVImportData | null;
  error: string | null;
  loading: boolean;
  handleFileAccept: (files: File[]) => Promise<CSVImportData | null>;
  reset: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

export function useCSVImport(): UseCSVImportReturn {
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<CSVImportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleFileAccept = useCallback(async (files: File[]): Promise<CSVImportData | null> => {
    const selectedFile = files[0];
    if (!selectedFile) return null;

    setFile(selectedFile);
    setLoading(true);
    setError(null);
    setData(null);

    try {
      const text = await selectedFile.text();
      const parsed = parseCSVText(text);
      setData(parsed);
      return parsed;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nie udało się przetworzyć pliku CSV');
      setData(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { file, data, error, loading, handleFileAccept, reset };
}
